import { forwardRef, memo, ReactNode, Ref, useCallback, useEffect, useMemo, useState } from "react";
import classNames from "classnames";
import { Accept } from "react-dropzone";
import { useRefEffect } from "../../../../utils";
import { FileUploadField } from "../../../form";
import { Link, LoaderContainer } from "../../../ui";
import { SpreadsheetHighlightContext, SpreadsheetHighlightContextProvider } from "../SpreadsheetHighlightContext";
import { SpreadsheetView } from "../SpreadsheetView";
import { loadFile } from "./utils";

import { ReactComponent as ReloadIcon } from "./icons/icon-repeat.svg";

import styles from "./style.module.scss";

const SUPPORTED_FORMATS: Accept = {
  "text/csv": [ ".csv" ],
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [ ".xlsx" ],
};

export type SpreadsheetUploaderRefType = {
  loadData: () => Promise<(any[])[] | null>
  resetFile: () => void,
  getFile: () => File | null,
} | undefined

export type SpreadsheetUploaderProps = {
  children: ReactNode | ReactNode[],
  className?: string,
  contentClassName?: string,
  onPreviewLoaded?: (data: (any[])[] | null) => (Promise<void> | void),
  ref?: Ref<SpreadsheetUploaderRefType>,
  disabled?: boolean,
}

export const SpreadsheetUploader = memo<SpreadsheetUploaderProps>(forwardRef<SpreadsheetUploaderRefType, SpreadsheetUploaderProps>((
  {
    children,
    className,
    contentClassName,
    onPreviewLoaded,
    disabled,
  }, ref) => {
  const [ file, setFile ] = useState<File | null>(null);
  const [ preview, setFilePreview ] = useState<(any[])[] | null>();
  const isLoading = !!file && !preview;

  const loadData = useCallback(async () => await loadFile(file), [ file ]);
  const resetFile = useCallback(() => setFile(null), []);
  const getFile = useCallback(() => file, [ file ]);
  const refInfo = useMemo(() => ({ loadData, resetFile, getFile }), [ loadData, resetFile, getFile ]);

  useRefEffect<SpreadsheetUploaderRefType, SpreadsheetUploaderRefType>(ref, refInfo);

  useEffect(() => {
    let isMounted = true;

    new Promise<void>(async resolve => {
      const data = await loadFile(file, 100);

      if (isMounted) {
        setFilePreview(data);
        onPreviewLoaded?.(data);
      }

      resolve();
    });

    return () => {
      isMounted = false;
    };
  }, [ file, onPreviewLoaded ]);

  return (
    <div className={classNames(styles.root, className)}>
      <LoaderContainer loaderClassName={styles.content} isLoading={isLoading}>
        {
          preview ?
            <>
              <Link
                className={styles.reload}
                component="button"
                type="button"
                linkType="s"
                onClick={() => {
                  setFilePreview(null);
                  setFile(null);
                }}
                disabled={disabled}
              >
                <ReloadIcon /> Reload file
              </Link>
              <div className={classNames(styles.content, styles.spreadsheet)}>
                <SpreadsheetHighlightContextProvider>
                  <SpreadsheetHighlightContext.Consumer>
                    {
                      ({ highlightedColumns, highlightedRows }) => (
                        <SpreadsheetView
                          data={preview}
                          highlightedRows={highlightedRows}
                          highlightedColumns={highlightedColumns}
                        />
                      )
                    }
                  </SpreadsheetHighlightContext.Consumer>
                  <div className={contentClassName}>
                    {children}
                  </div>
                </SpreadsheetHighlightContextProvider>
              </div>
            </>
            :
            <FileUploadField
              className={styles.content}
              accept={SUPPORTED_FORMATS}
              value={file}
              onChange={files => setFile(files as File)}
              showFileNames
              disabled={disabled}
            />
        }
      </LoaderContainer>
    </div>
  );
}));

export default SpreadsheetUploader;
